/*
 * @description: Don't description
 * @Author: Mustafa.Yin
 * @Date: 2025-08-24 18:38:30
 * @FilePath: \smart-reco-portal\src\utils\sessionManager.js
 * @LastEditTime: 2026-06-19 15:42:00
 */
// utils/sessionManager.js

import { setStore, getStore, removeStore } from '@/utils/store';

let userSessionId = null;
let portalToken = null;
let portalTokenExpire = 0;

const PORTAL_TOKEN_KEY = 'portalToken';
const PORTAL_TOKEN_EXPIRE_KEY = 'portalTokenExpire';

// 生成一个简单的唯一ID（可用 uuid / nanoid 替代）
function generateSessionId() {
  return 'portal_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
}

// 初始化 userSessionId（仅在首次访问时生成）
export function initUserSessionId() {
  if (!userSessionId) {
    // 判断是否已初始化（通过 sessionStorage）
    const isInitialized = sessionStorage.getItem('h5_session_initialized');

    if (!isInitialized) {
      userSessionId = generateSessionId();
      // 存储 userSessionId 到 sessionStorage（可选，如果需要在刷新后仍可用）
      sessionStorage.setItem('userSessionId', userSessionId);
      // 标记已初始化，防止重复生成
      sessionStorage.setItem('h5_session_initialized', 'true');
      console.log('[Session] 首次访问，生成 userSessionId:', userSessionId);
    } else {
      // 非首次访问，从 sessionStorage 读取
      userSessionId = sessionStorage.getItem('userSessionId') || null;
      console.log('[Session] 非首次访问，复用 userSessionId:', userSessionId);
    }
  }
  return userSessionId;
}

// 获取当前 userSessionId
export function getUserSessionId() {
  if (!userSessionId) {
    return initUserSessionId(); // 懒初始化
  }
  return userSessionId;
}

// 从存储读取 token（页面刷新后恢复）
function loadStoredToken() {
  const stored = getStore({ name: PORTAL_TOKEN_KEY });
  const expire = getStore({ name: PORTAL_TOKEN_EXPIRE_KEY });
  if (stored && expire && expire > Date.now()) {
    portalToken = stored;
    portalTokenExpire = expire;
  } else if (stored) {
    clearPortalToken();
  }
}

function clearPortalToken() {
  portalToken = null;
  portalTokenExpire = 0;
  removeStore({ name: PORTAL_TOKEN_KEY, type: 'session' });
  removeStore({ name: PORTAL_TOKEN_EXPIRE_KEY, type: 'session' });
}

/**
 * 获取 portal 会话令牌：本地有未过期 token 直接复用，否则请求 initSession
 * @param {string} mdmNo 可选，医院 MDM 编号
 * @returns {Promise<string>}
 */
export async function ensurePortalToken(mdmNo) {
  if (!portalToken) loadStoredToken();
  if (portalToken && portalTokenExpire > Date.now() + 60 * 1000) {
    return portalToken;
  }

  const sid = getUserSessionId();
  const params = new URLSearchParams({ userSessionId: sid });
  if (mdmNo) params.append('mdmNo', mdmNo);

  const base = process.env.VUE_APP_BASE_API || '';
  const resp = await fetch(`${base}smartreco/api/initSession?${params.toString()}`);
  const text = await resp.text();
  const result = JSON.parse(text);
  if (!result || result.code !== 200 || !result.data || !result.data.token) {
    throw new Error('initSession 失败: ' + (result && result.msg ? result.msg : '未知错误'));
  }
  portalToken = result.data.token;
  portalTokenExpire = result.data.expireTime || 0;
  setStore({ name: PORTAL_TOKEN_KEY, content: portalToken, type: 'session' });
  setStore({ name: PORTAL_TOKEN_EXPIRE_KEY, content: portalTokenExpire, type: 'session' });
  return portalToken;
}

/**
 * 销毁会话（业务结束调用）
 */
export function destroyUserSessionId() {
  userSessionId = null;
  clearPortalToken();
  sessionStorage.removeItem('userSessionId');
  sessionStorage.removeItem('h5_session_initialized');
  console.log('[Session] userSessionId 已销毁');
}

/**
 * portal 受保护接口专用 fetch：自动注入 Authorization，401 自动重试一次
 * @param {string} url 接口路径（相对 VUE_APP_BASE_API）
 * @param {object} options fetch options
 * @param {string} mdmNo 可选
 */
export async function portalFetch(url, options = {}, mdmNo) {
  const token = await ensurePortalToken(mdmNo);
  const headers = Object.assign({ Authorization: `Bearer ${token}` }, options.headers || {});
  const base = process.env.VUE_APP_BASE_API || '';
  const fullUrl = url.startsWith('http') ? url : `${base}${url}`;

  let resp = await fetch(fullUrl, Object.assign({}, options, { headers }));
  // 401 自动重新 initSession 并重试一次
  if (resp.status === 401) {
    clearPortalToken();
    const newToken = await ensurePortalToken(mdmNo);
    const retryHeaders = Object.assign({ Authorization: `Bearer ${newToken}` }, options.headers || {});
    resp = await fetch(fullUrl, Object.assign({}, options, { headers: retryHeaders }));
  }
  return resp;
}
